/**
 * @file Module to create PillGroup component.
 */

import { Pill } from './Pill';

/**
 * Class representing a pill.
 */
export class PillGroup {

  constructor(props) {
    this.dom = {
      element: props.element,
      selectAll: undefined
    };

    this.type = props.type || 'filter';
    this.action = props.action || (() => {});

    this.pills = [];
    this.allPillsSelected = true;
    this.lessThanTwoPills = true;
    this.lastSelectedBitmask = [];
  }

  /**
   * Initialize the pill group.
   * @public
   */
  init() {
    const data = [];
    Array.from(this.dom.element.querySelectorAll('.pill')).forEach(pill => {
      const dataset = {};
      Object.keys(pill.dataset).forEach(d => dataset[d] = pill.dataset[d]);

      data.push({
        element: pill,
        dataset,
        selected: !pill.classList.contains('unselected'),
        disabled: pill.classList.contains('disabled'),
        toggle: pill.classList.contains('toggle')
      });
    });

    switch (this.type) {
      case 'radio':
        this._initRadio(data);
        break;
      case 'checkbox':
        this._initCheckbox(data);
        break;
      default:
        this._initFilter(data);
    }
  }

  /**
   * Destroy the pill group and associated events.
   * @public
   */
  destroy() {
    this.pills.forEach(pill => pill.destroy());
    this.pills = [];
  }

  /**
   * Get a reference to all the pills.
   * @public
   */
  getPills() {
    return this.pills;
  }

  /**
   * Init radio behaviour.
   */
  _initRadio(data) {
    let selectedCount = 0;

    data.forEach((pData, index) => {
      if (pData.selected === false || selectedCount > 0) {
        pData.element.classList.add('unselected');
      }

      if (pData.disabled !== true && pData.selected !== false) {
        selectedCount += 1;
      }

      const pill = new Pill(pData.element, this._enforcePillRadioBehaviour(index, this.action));
      pill.setData(pData.dataset);
      pill.init();
      this.pills.push(pill);
    });

    this.lastSelectedBitmask = this.pills.map(pill => pill.isSelected());

    if (selectedCount === 0) {
      for (let i = 0; i < this.pills.length; i += 1) {
        if (this.pills[i].disabled !== true) {
          this.pills[i].select();
          break;
        }
      }
    }
  }

  /**
   * Init checkbox behaviour.
   */
  _initCheckbox(data) {
    let selectedCount = 0;
    let pillCount = 0;

    data.forEach((pData, index) => {
      if (pData.selected === false) {
        pData.element.classList.add('unselected');
      }

      if (pData.disabled !== true) {
        pillCount += 1;
      }
      if (pData.disabled !== true && pData.selected !== false) {
        selectedCount += 1;
      }

      const pill = new Pill(pData.element, this._enforcePillCheckboxBehaviour(index, this.action));
      pill.setData(pData.dataset);
      pill.init();
      this.pills.push(pill);
    });

    this.allPillsSelected = false;
    if (pillCount === selectedCount) {
      this.allPillsSelected = true;
    }

    this.dom.selectAll = this.dom.element.querySelector('.toggle-all') || undefined;
    if (this.dom.selectAll !== undefined) {
      if (this.allPillsSelected) {
        this._hideSelectAll();
      }
      this.dom.selectAll.addEventListener('click', () => {
        this.selectAllPills();
        this.action(this.pills);
      });
    }
  }

  /**
   * Init filter behaviour.
   * @private
   */
  _initFilter(data) {
    let selectedCount = 0;
    let notSelectedCount = 0;
    let notDisabledCount = 0;

    data.forEach(pData => {
      if (pData.disabled !== true) {
        if (pData.selected === false) {
          notSelectedCount += 1;
        } else {
          selectedCount += 1;
        }
        notDisabledCount += 1;
      }
    });

    const allPillsSelected = notSelectedCount === 0 && selectedCount > 0;
    const allPillsUnselected = notSelectedCount > 0 && selectedCount === 0;

    if (notDisabledCount > 1) {
      this.lessThanTwoPills = false;
    }
    if (allPillsSelected || allPillsUnselected) {
      this.allPillsSelected = true;
    } else {
      this.allPillsSelected = false;
    }

    data.forEach((pData, index) => {
      if (this.allPillsSelected) {
        pData.element.classList.remove('unselected');
      }

      const pill = new Pill(pData.element, this._enforcePillFilterBehaviour(index, this.action));
      pill.setData(pData.dataset);
      pill.init();
      this.pills.push(pill);
    });

    this.lastSelectedBitmask = this.pills.map(pill => pill.isSelected());

    this.dom.selectAll = this.dom.element.querySelector('.toggle-all') || undefined;
    if (this.dom.selectAll !== undefined) {
      if (this.allPillsSelected) {
        this._hideSelectAll();
      }
      this.dom.selectAll.addEventListener('click', () => {
        this.selectAllPills();
        this.action(this.pills);
      });
    }
  }

  /**
   * Hide select all link.
   * @private
   */
  _hideSelectAll() {
    if (this.dom.selectAll !== undefined) {
      this.dom.selectAll.style.display = 'none';
    }
  }

  /**
   * Show select all link.
   * @private
   */
  _showSelectAll() {
    if (this.dom.selectAll !== undefined) {
      this.dom.selectAll.style.display = '';
    }
  }

  /**
   * Select all pills.
   * @private
   */
  selectAllPills() {
    if (this.type !== 'filter' && this.type !== 'checkbox') return;
    this.allPillsSelected = true;
    this.pills.forEach(pill => {
      if (!pill.isDisabled()) {
        pill.select();
      }
    });
    this._hideSelectAll();
  }

  /**
   * Enforces the new pill radio behaviour.
   * @private
   */
  _enforcePillRadioBehaviour(index, callback) {
    return () => {
      this.pills.forEach((pill, i) => {
        if (!pill.isDisabled()) {
          if (index === i) {
            pill.select();
          } else {
            pill.unselect();
          }
        }
      });

      const bitmaskChanged = this.lastSelectedBitmask.filter((d, i) => d !== this.pills[i].isSelected()).length > 0;
      this.lastSelectedBitmask = this.pills.map(pill => pill.isSelected());
      if (bitmaskChanged && callback) {
        callback(this.pills);
      }
    };
  }

  /**
   * Enforces the new pill checkbox behaviour.
   * @private
   */
  _enforcePillCheckboxBehaviour(index, callback) {
    return () => {
      let pillCount = 0;
      let selectedCount = 0;
      this.pills.forEach(pill => {
        if (!pill.isDisabled()) {
          pillCount += 1;
          if (pill.isSelected()) {
            selectedCount += 1;
          }
        }
      });

      if (pillCount === selectedCount) {
        this.allPillsSelected = true;
        this._hideSelectAll();
      } else {
        this._showSelectAll();
      }

      if (callback) {
        callback(this.pills);
      }
    };
  }

  /**
   * Enforces the new pill filter behaviour.
   * @private
   */
  _enforcePillFilterBehaviour(index, callback) {
    return () => {
      if (this.allPillsSelected && !this.lessThanTwoPills)
      {
        this.pills.forEach((pill, i) => {
          if (!pill.isDisabled()) {
            if (index !== i) {
              pill.unselect();
            } else {
              pill.select();
            }
          }
        });

        this.allPillsSelected = false;
        this._showSelectAll();
      }
      else
      {
        let allPillsUnselected = true;
        this.allPillsSelected = true;
        this._hideSelectAll();

        this.pills.forEach(pill => {
          if (!pill.isDisabled()) {
            if (pill.isSelected() === false) {
              this.allPillsSelected = false;
              this._showSelectAll();
            } else {
              allPillsUnselected = false;
            }
          }
        });

        if (allPillsUnselected) {
          this.selectAllPills();
        } else if (this.allPillsSelected) {
          this._hideSelectAll();
        }
      }

      const bitmaskChanged = this.lastSelectedBitmask.filter((d, i) => d !== this.pills[i].isSelected()).length > 0;
      this.lastSelectedBitmask = this.pills.map(pill => pill.isSelected());
      if (bitmaskChanged && callback) {
        callback(this.pills);
      }
    };
  }

}
